import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

/**
 * The GLOBAL vehicle list (slice 18f). Mahad: *"I don't see how many vehicles
 * registered."* and *"I want as admin to see all what are under my watch —
 * tenant, vehicle assigned to tenant, devices, simcards."*
 *
 * Types mirror `AdminVehicleZ` in `api/src/modules/admin/routes.ts` (seam S1)
 * and are never re-declared from the screen's point of view.
 *
 * 🔴 THERE IS NO `imei` ON THIS WIRE AND THERE MUST NEVER BE ONE.
 * This route is `requireStaff` — a support hire needs the fleet list — while
 * every route that returns an IMEI is `requireSuperAdmin`. Carrying the IMEI
 * here would make that gate decoration. `deviceId` (the register row uuid)
 * travels instead, so a super-admin can follow it to `/devices` and read the
 * IMEI behind the right guard.
 */

export interface AdminVehicle {
  vehicleId: string;
  plate: string;
  label: string | null;
  /** SLICE 56c — the vehicle's model (e.g. `Toyota Fielder`), editable in the edit
   *  dialog. Carried on the list so that dialog prefills it. */
  model: string | null;
  vehicleType: string | null;
  /** SLICE 83 — `vehicles.report_period_sec`; null = the 300 s default. Carried so
   *  the edit dialog prefills it. */
  reportPeriodSec?: number | null;
  plan: 'track' | 'fleet' | 'pro';
  /**
   * NULL = this vehicle has never been cleared (the go-live gate). It is NOT on
   * its own the visibility answer — see `hiddenAt` and `isCustomerVisible`.
   */
  liveAt: string | null;
  /**
   * 18g — NON-NULL = cleared once and since WITHDRAWN. `liveAt` is deliberately
   * never rewritten, because it records when the customer FIRST saw the vehicle,
   * so the two columns together are the only honest answer to "can they see it".
   */
  hiddenAt: string | null;
  createdAt: string;
  /** 18g — NULL when no tracker is fitted. Never coerced to 0: Traccar device 0
   *  is the ungrouped sentinel and would render as a real device id. */
  traccarDeviceId: number | null;
  /** The `devices` row uuid — the link to `/devices`. NOT the IMEI. */
  deviceId: string | null;
  tenantId: string;
  tenantName: string;
  tenantStatus: 'active' | 'past_due' | 'suspended';
  /**
   * Slice 30 — the install-record state of the vehicle's OPEN assignment,
   * computed server-side in the list query (never per row in JS):
   *   `no_tracker`  no open assignment
   *   `none`        an assignment with no install record
   *   `unconfirmed` a record whose `verified_live_at` is null
   *   `confirmed`   a record confirmed live
   */
  installStatus: 'no_tracker' | 'none' | 'unconfirmed' | 'confirmed';
}

/**
 * 🔴 THE VISIBILITY PREDICATE, IN ONE PLACE (18g).
 *
 * `liveAt !== null` alone was the answer until this slice and is now WRONG: a
 * withdrawn vehicle still carries the `liveAt` it was first cleared at. Every
 * surface that states "the customer can see this" reads THIS function, so the
 * count line, the filter and the row pill cannot disagree with each other or with
 * `IS_CUSTOMER_VISIBLE` in `api/src/db/schema/index.ts`, which they mirror.
 */
export function isCustomerVisible(v: Pick<AdminVehicle, 'liveAt' | 'hiddenAt'>): boolean {
  return v.liveAt !== null && (v.hiddenAt ?? null) === null;
}

/**
 * Cleared once, and since WITHDRAWN — the third state the list must show.
 *
 * 🔴 `?? null` IS DELIBERATE IN BOTH, AND THE DIRECTION IS CHOSEN, NOT INCIDENTAL.
 * The field is required on the wire, so `undefined` should never arrive — but if
 * it ever does (an older API, a degraded body, a hand-built fixture), a bare
 * `!== null` reads `undefined` as HIDDEN and every live vehicle on the platform
 * is reported as withdrawn from its customer. Coalescing first makes the missing
 * case "not hidden", which is the same choice `hasTracker` makes on the customer
 * side and for the same reason: pick the default whose failure is the smaller lie.
 */
export function isHidden(v: Pick<AdminVehicle, 'liveAt' | 'hiddenAt'>): boolean {
  return v.liveAt !== null && (v.hiddenAt ?? null) !== null;
}

export interface AdminVehicleList {
  vehicles: AdminVehicle[];
  /** Counted from the rows the response carries, so it cannot disagree. */
  total: number;
  liveCount: number;
}

export const adminVehiclesQueryKey = ['admin', 'vehicles'] as const;

export function useAdminVehicles(): UseQueryResult<AdminVehicleList> {
  return useQuery({
    queryKey: adminVehiclesQueryKey,
    queryFn: ({ signal }) => apiJson<AdminVehicleList>('/api/admin/vehicles', { signal }),
    // A 4xx is a decision, not a hiccup — retrying only delays the answer.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}
